<template>
  <li
    :class="rootClass"
    tabindex="0"
    @click="selectClip"
    @keydown.enter.prevent="selectClip"
    @keydown.space.prevent="selectClip"
  >
    <div class="h-1 w-full" :style="accentStyle" />

    <div class="px-2 pt-2">
      <div v-if="isClipLoaded" class="flex min-w-0 justify-between items-center gap-1">
        <div class="min-w-0 flex-1">
          <input
            v-model="clipName"
            type="text"
            :maxlength="VIDEO_EDITOR_CLIP_NAME_MAX_LENGTH"
            class="video-editor-clip-name block w-full appearance-none !bg-transparent text-sm font-medium leading-tight text-white !border-0 !outline-none !ring-0 !shadow-none p-0 focus:!bg-transparent focus:!outline-none focus:!ring-0 focus:!shadow-none"
            @keydown.stop
            @focus="selectClip"
            @blur="updateClipName"
            data-onboarding-video-snipping="clip-name"
          >
        </div>
        <div
          v-if="clipDurationLabel"
          class="shrink-0 rounded-[4px] bg-black/65 p-1.5 py-1 text-xs font-medium leading-none text-white tabular-nums"
        >
          {{ clipDurationLabel }}
        </div>
        <core-tooltip text="Delete Clip" :popper="{placement: 'top'}">
          <button
            type="button"
            class="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-md text-red-400 transition-colors hover:text-red-300"
            aria-label="Delete Clip"
            data-onboarding-video-snipping="clip-delete"
            @click.stop="confirmDeleteClip"
          >
            <u-icon :name="COMMON_ICONS.delete" class="text-lg leading-none" />
          </button>
        </core-tooltip>
      </div>
      <div v-else class="flex min-w-0 items-center justify-between gap-2">
        <div class="clip-marker-shimmer h-4 flex-1 rounded-[4px]" aria-hidden="true" />
        <div class="clip-marker-shimmer h-6 w-16 shrink-0 rounded-[4px]" aria-hidden="true" />
      </div>
    </div>

    <div class="px-2 py-2 bg-charcoal-900/60">
      <div class="flex min-w-0 items-start gap-2">
        <div class="flex min-w-0 flex-1 items-center gap-2">
          <template v-for="(item, idx) in previewItems" :key="item.id">
            <div class="min-w-0 flex-1">
              <div class="relative w-full overflow-hidden rounded-md" :style="previewFrameStyle">
                <u-dropdown
                  v-if="item.showTitleFrameMenu"
                  :items="titleFrameMenuItems"
                  :popper="{ placement: 'bottom-end' }"
                  class="absolute right-2 bottom-1 z-10 h-6"
                >
                  <u-button
                    class="flex items-center justify-center rounded-md text-white bg-black/65 p-1"
                    aria-label="Title Cover Menu"
                    variant="link"
                    size="md"
                    color="white"
                    @pointerdown.stop
                  >
                    <u-icon name="i-ri-image-edit-fill" class="text-lg" />
                  </u-button>
                </u-dropdown>
                <img
                  v-if="item.imageSrc"
                  :src="item.imageSrc"
                  draggable="false"
                  class="block h-full w-full object-contain"
                >
                <div
                  v-else
                  class="clip-marker-shimmer absolute inset-0"
                  aria-hidden="true"
                />
                <div
                  v-if="isClipLoaded"
                  class="absolute left-1.5 bottom-1 h-6 rounded-md flex-center bg-black/65 px-1.5  text-xs font-medium leading-none text-white tabular-nums"
                >
                  {{ item.timeLabel }}
                </div>
              </div>
            </div>

            <div
              v-if="idx === 0 && hasMultiplePreviewMarkers"
              class="self-stretch flex items-center text-gray-400 shrink-0"
              aria-hidden="true"
            >
              <u-icon name="i-ri-arrow-right-line" class="text-sm" />
            </div>
          </template>
        </div>
      </div>
      <button
        v-if="isClipLoaded && !hasTitleFrame"
        type="button"
        class="mt-2 w-full rounded-md border border-white/20 py-1.5 font-medium text-white transition-colors hover:border-white/40 hover:text-white/90 flex-center gap-2"
        @click.stop="openTitleFrameEditor"
      >
        <u-icon name="i-ri-image-edit-fill" class="w-4 h-4" />
        Create Title Cover
      </button>
    </div>
  </li>
</template>

<script setup>
  const props = defineProps({
    clip: {
      type: Object,
      required: true
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    previewAspectRatio: {
      type: Number,
      default: 16 / 9
    }
  });

  const formatTime = inject('formatTime');
  const {
    editCustomFrame: editCustomFrameAction,
    deleteCustomFrame: deleteCustomFrameAction,
    deleteClip: deleteClipAction
  } = useVideoEditorActions();
  const videoEditorStore = useVideoEditorStore();

  const rootClass = computed(() => ([
    'relative overflow-hidden rounded-md text-white bg-charcoal-800 shadow-md border transition-colors',
    props.isSelected ? 'border-white/80' : 'border-transparent hover:border-white/30'
  ]));

  const accentStyle = computed(() => ({
    background: isClipLoaded.value ? props.clip?.color : 'rgba(255, 255, 255, 0.2)'
  }));

  const previewFrameStyle = computed(() => ({
    aspectRatio: String(props.previewAspectRatio || (16 / 9))
  }));

  const clipName = computed({
    get: () => props.clip?.name || '',
    set: (value) => {
      if (props.clip) {
        props.clip.name = value;
      }
    }
  });

  const sortedMarkers = computed(() =>
    (props.clip?.markers || []).slice().sort((a, b) => a.time - b.time)
  );

  const titleFrame = computed(() => {
    const frame = props.clip?.frames?.title;
    return frame?.design_project_id != null ? frame : null;
  });

  const hasTitleFrame = computed(() => Boolean(titleFrame.value));

  const clipDuration = computed(() => getClipLengthSeconds(props.clip));

  const clipDurationLabel = computed(() =>
    clipDuration.value === null ? null : formatSecondsToVideoEditorTime(clipDuration.value)
  );

  const previewItems = computed(() => {
    const markers = sortedMarkers.value.slice(0, 2);
    const fallbackTime = Number(markers[0]?.time) || 0;
    const placeholderCount = Math.max(0, 2 - markers.length);
    const placeholders = Array.from({length: placeholderCount}, (_, idx) => ({
      id: `${String(props.clip?.id || 'clip')}-placeholder-${idx}`,
      time: fallbackTime,
      snapshot: null
    }));
    const slots = markers.length >= 2 ? markers : [...markers, ...placeholders];

    return slots.map((marker, idx) => ({
      id: marker.id,
      time: marker.time,
      timeLabel: formatTime(marker.time),
      imageSrc: resolvePreviewImageSrc({marker, idx}),
      showTitleFrameMenu: idx === 0 && hasTitleFrame.value
    }));
  });

  const hasMultiplePreviewMarkers = computed(() => previewItems.value.length > 1);
  const isClipLoaded = computed(() =>
    previewItems.value.length >= 2 && previewItems.value.every(item => Boolean(item.imageSrc))
  );
  const titleFrameMenuItems = computed(() => [[
    {
      label: 'Edit Title Cover',
      icon: 'i-ri-image-edit-fill',
      click: () => openTitleFrameEditor()
    },
    {
      label: 'Delete Title Cover',
      icon: COMMON_ICONS.delete,
      click: () => confirmDeleteTitleFrame()
    }
  ]]);

  function markerHasSnapshot(marker) {
    return typeof marker?.snapshot === 'string' && marker.snapshot.trim().length > 0;
  }

  function frameHasThumbnail(frame) {
    return typeof frame?.thumbnail === 'string' && frame.thumbnail.trim().length > 0;
  }

  function resolvePreviewImageSrc({marker, idx}) {
    if (idx === 0 && frameHasThumbnail(titleFrame.value)) {
      return titleFrame.value.thumbnail;
    }

    return markerHasSnapshot(marker) ? marker.snapshot : null;
  }

  function openTitleFrameEditor() {
    editCustomFrameAction({
      clip: props.clip,
      frame: titleFrame.value
    });
  }

  function confirmDeleteTitleFrame() {
    if (!titleFrame.value) {
      return;
    }

    useConfirmModal({
      heading: 'Delete Title Cover',
      message: 'Are you sure you want to delete this title cover?',
      confirmText: 'Delete',
      color: 'red',
      confirmMethod: async () => {
        try {
          await deleteCustomFrameAction({
            clip: props.clip,
            frame: titleFrame.value
          });
        } catch (e) {
          useErrorToast().add();
        }
      }
    }).open();
  }

  function confirmDeleteClip() {
    useConfirmModal({
      heading: 'Delete Clip',
      message: 'Are you sure you want to delete this clip?',
      confirmText: 'Delete',
      color: 'red',
      confirmMethod: async () => {
        try {
          await deleteClipAction({clip: props.clip});
        } catch (e) {
          useErrorToast().add();
        }
      }
    }).open();
  }

  function updateClipName() {
    videoEditorStore.updateCLip({
      clip: props.clip,
      updates: {
        name: clipName.value
      }
    });
  }

  function selectClip() {
    videoEditorStore.setActiveEditClip({
      clipId: props.clip?.id ?? null
    });
  }
</script>

<style scoped>
  .video-editor-clip-name {
    background: transparent !important;
    background-color: transparent !important;
    box-shadow: none !important;
    border: 0 !important;
    outline: 0 !important;
  }

  .video-editor-clip-name:focus {
    background: transparent !important;
    background-color: transparent !important;
    box-shadow: none !important;
    border: 0 !important;
    outline: 0 !important;
  }

  .video-editor-clip-name::-webkit-outer-spin-button,
  .video-editor-clip-name::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  @keyframes clip-item-shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  .clip-marker-shimmer {
    background-color: rgba(255, 255, 255, 0.08);
    background-image: linear-gradient(
      110deg,
      rgba(255, 255, 255, 0.06) 20%,
      rgba(255, 255, 255, 0.24) 38%,
      rgba(255, 255, 255, 0.06) 55%
    );
    background-size: 220% 100%;
    animation: clip-item-shimmer 1.25s linear infinite;
  }
</style>
